import React, { useMemo, useState } from 'react';
import { graphql } from 'gatsby';
import GridSection, {
    GridSectionProps,
} from 'design-system/src/components/organisms/GridSection';

import { useTranslate } from 'gatsby-theme-soluble-source';

import { formatResource } from '../../utils/resources';

interface ResourcesSectionProps {
    itemsEbooks: any[];
    itemsEvents: any[];
    itemsMethodologies: any[];
    itemsPodcasts: any[];
    itemsWebinars: any;
    resourceStatus: 'all' | 'upcoming' | 'ondemand';
    title: string;
    titleTag: GridSectionProps['titleTag'];
    cta: any;
    text: string;
    podcasts: any[];
    events: any[];
    webinars: any[];
    methodologies: any[];
    ebooks: any[];
    successStories: any[];
    itemsSuccessStories: any[];
    kind:
        | 'events'
        | 'webinars'
        | 'methodologies'
        | 'podcasts'
        | 'ebooks'
        | 'success_stories';
    itemsLimit: number | null;
}

const ResourcesSection = ({
    itemsEbooks,
    itemsEvents,
    itemsMethodologies,
    itemsPodcasts,
    itemsWebinars,
    resourceStatus,
    title,
    titleTag,
    cta,
    text,
    podcasts,
    events,
    webinars,
    methodologies,
    ebooks,
    successStories,
    itemsSuccessStories,
    kind,
    itemsLimit,
}: ResourcesSectionProps) => {
    const _ = useTranslate();

    const [currentPage, setCurrentPage] = useState(1);

    const handleClickPagination = () => {
        setCurrentPage((prev) => prev + 1);
    };

    const gridVariant = useMemo(() => {
        const gridVariantByKind: Record<
            ResourcesSectionProps['kind'],
            GridSectionProps['variant']
        > = {
            success_stories: 'grid-content-1',
            events:
                resourceStatus === 'ondemand'
                    ? 'grid-media-1'
                    : 'cards-wrapper',
            webinars:
                resourceStatus === 'ondemand'
                    ? 'grid-media-1'
                    : 'cards-wrapper',
            methodologies: 'grid-content-1',
            podcasts: 'grid-info-3',
            ebooks: 'grid-content-1',
        };

        return gridVariantByKind?.[kind] ?? 'grid-content-1';
    }, [kind, resourceStatus]);

    const pagination = useMemo(() => {
        if (!itemsLimit) {
            return {
                pagination: {
                    initialItems: 6,
                    itemsPerPage: 6,
                    page: currentPage,
                },
                viewMore: {
                    label: _['cta.label.viewmore'].text,
                    onClick: handleClickPagination,
                },
            };
        }

        return {};
    }, [_, currentPage, itemsLimit]);

    const thisItems = useMemo(() => {
        let items = [];

        switch (kind) {
            case 'events':
                items = [...events, ...itemsEvents].map((item) =>
                    formatResource(item, 'event', _),
                );
                break;
            case 'webinars':
                items = [...webinars, ...itemsWebinars].map((item) =>
                    formatResource(item, 'webinar', _),
                );
                break;
            case 'methodologies':
                items = [...methodologies, ...itemsMethodologies].map((item) =>
                    formatResource(item, 'methodology', _),
                );
                break;
            case 'podcasts':
                items = [...podcasts, ...itemsPodcasts].map((item) =>
                    formatResource(item, 'podcast', _),
                );
                break;
            case 'ebooks':
                items = [...ebooks, ...itemsEbooks].map((item) =>
                    formatResource(item, 'ebook', _),
                );
                break;
            case 'success_stories':
                items = [...successStories, ...itemsSuccessStories].map(
                    (item) => formatResource(item, 'successStory', _),
                );
                break;
            default:
                items = [];
                break;
        }

        return items;
    }, [
        kind,
        events,
        itemsEvents,
        webinars,
        itemsWebinars,
        methodologies,
        itemsMethodologies,
        podcasts,
        itemsPodcasts,
        ebooks,
        itemsEbooks,
        successStories,
        itemsSuccessStories,
        _,
    ]);

    return (
        <GridSection
            variant={gridVariant}
            title={title}
            titleTag={titleTag}
            cta={cta}
            text={text}
            items={thisItems}
            {...pagination}
        />
    );
};

export default ResourcesSection;

export const query = graphql`
    fragment MethodologyItem on DatoCmsMethodology {
        date: dateFormatted
        text: description
        href: externalLink
        id
        title
    }
    fragment PodcastItem on DatoCmsPodcast {
        title
        text: description
        episodeId
        spotifyUrl
        ivooxUrl
        appleUrl
    }
    fragment EbookItem on DatoCmsEbook {
        text: description
        href: externalLink
        id
        title
        image {
            ...SourceMedia
            gatsbyImageData(width: 660, placeholder: BLURRED)
        }
    }
    fragment SuccessStoryItem on DatoCmsSuccessStory {
        title
        category
        text: description
        href: externalLink
        id
        image {
            ...SourceMedia
            gatsbyImageData(
                placeholder: BLURRED
                height: 850
                width: 850
                imgixParams: { fit: "crop", w: "850", h: "850" }
            )
        }
        author {
            name
            image {
                ...SourceMedia
                gatsbyImageData(
                    placeholder: BLURRED
                    height: 32
                    width: 32
                    imgixParams: {
                        fit: "crop"
                        crop: "faces"
                        w: "32"
                        h: "32"
                    }
                )
            }
        }
    }
    fragment EventItem on DatoCmsEvent {
        title
        date: dateFormatted
        text: description
        href: eventLink
        location
        outdated
        media {
            ...SourceMedia
            gatsbyImageData(
                placeholder: BLURRED
                height: 720
                width: 1280
                imgixParams: { fit: "crop", w: "1280", h: "720" }
            )
        }
    }
    fragment WebinarItem on DatoCmsWebinar {
        date: dateFormatted
        text: description
        href: eventLink
        tag: category
        image {
            ...SourceMedia
            gatsbyImageData(width: 660, placeholder: BLURRED)
        }
        title
        speakers {
            name
            image {
                ...SourceMedia
                gatsbyImageData(
                    placeholder: BLURRED
                    height: 32
                    width: 32
                    imgixParams: {
                        fit: "crop"
                        crop: "faces"
                        w: "32"
                        h: "32"
                    }
                )
            }
        }
    }
    fragment DatoCmsResourcesSection on DatoCmsResourcesSection {
        title
        titleTag
        text
        cta {
            ...Button
        }
        kind
        resourceStatus
        events {
            id
            typename: __typename
            ...EventItem
        }
        itemsEvents {
            id
            typename: __typename
            ...EventItem
        }
        webinars {
            id
            typename: __typename
            ...WebinarItem
        }
        itemsWebinars {
            id
            typename: __typename
            ...WebinarItem
        }
        methodologies {
            id
            typename: __typename
            ...MethodologyItem
        }
        itemsMethodologies {
            id
            typename: __typename
            ...MethodologyItem
        }
        podcasts {
            id
            typename: __typename
            ...PodcastItem
        }
        itemsPodcasts {
            id
            typename: __typename
            ...PodcastItem
        }
        ebooks {
            id
            typename: __typename
            ...EbookItem
        }
        itemsEbooks {
            id
            typename: __typename
            ...EbookItem
        }
        successStories {
            id
            typename: __typename
            ...SuccessStoryItem
        }
        itemsSuccessStories {
            id
            typename: __typename
            ...SuccessStoryItem
        }
    }
`;
